// src/components/Dashboard/Controllers.js
import React, { useEffect, useState, useCallback } from "react";
import { db, auth, API_BASE_URL } from "../../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  deleteDoc,
  getDoc,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Settings, UserPlus, Crown } from "lucide-react";
import ErrorModal from "../ui/ErrorModal";

function Controllers({ familyID, onReturn }) {
  const [controllers, setControllers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isOnlyController, setIsOnlyController] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentUserAccountUUID, setCurrentUserAccountUUID] = useState(null);
  const [ownerUUIDs, setOwnerUUIDs] = useState([]);

  // Get current user's accountUUID on mount
  useEffect(() => {
    const getAccountUUID = async () => {
      const user = auth.currentUser;
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        setCurrentUserAccountUUID(tokenResult.claims.accountUUID);
      }
    };
    getAccountUUID();
  }, []);

  const loadControllers = useCallback(async () => {
    const controllersList = [];
    const controllersCollectionRef = collection(
      db,
      "familyInfo",
      familyID,
      "controllers"
    );

    try {
      // Load owners for this family
      const ownersCollectionRef = collection(db, "familyInfo", familyID, "owners");
      const ownersSnapshot = await getDocs(ownersCollectionRef);
      const owners = ownersSnapshot.docs.map(doc => doc.id);
      setOwnerUUIDs(owners);

      const snapshot = await getDocs(controllersCollectionRef);

      // First pass: collect all controllers and identify which need email lookup
      const uuidControllersToLookup = [];
      for (let docSnap of snapshot.docs) {
        const controllerId = docSnap.id;

        if (controllerId.includes('@')) {
          // Email-based controller (pending invite)
          controllersList.push({
            controllerId,
            email: controllerId,
            isPending: true,
            isUUIDBased: false
          });
        } else {
          // UUID-based controller - need to look up email via cloud function
          uuidControllersToLookup.push(controllerId);
          controllersList.push({
            controllerId,
            email: null, // Will be filled in after lookup
            isPending: false,
            isUUIDBased: true
          });
        }
      }

      // Look up emails for UUID-based controllers via cloud function
      if (uuidControllersToLookup.length > 0) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();

          // Fetch emails for all UUID controllers in parallel
          const emailLookups = uuidControllersToLookup.map(async (uuid) => {
            try {
              const response = await fetch(`${API_BASE_URL}/user/getEmailForControllerUUID`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ controllerUUID: uuid })
              });

              if (response.ok) {
                const data = await response.json();
                return { uuid, email: data.email };
              }
              return { uuid, email: null };
            } catch (error) {
              console.error(`Error fetching email for ${uuid}:`, error);
              return { uuid, email: null };
            }
          });

          const emailResults = await Promise.all(emailLookups);

          // Update controllersList with fetched emails
          for (const result of emailResults) {
            const controller = controllersList.find(c => c.controllerId === result.uuid);
            if (controller) {
              controller.email = result.email || result.uuid; // Fallback to UUID if no email found
            }
          }
        }
      }

      const activeControllers = controllersList.filter(c => !c.isPending);
      setIsOnlyController(activeControllers.length <= 1);

      setControllers(controllersList);
    } catch (error) {
      console.error("Error loading controllers:", error);
    }
  }, [familyID]);

  useEffect(() => {
    loadControllers();
  }, [loadControllers]);

  const inviteController = async () => {
    if (inviteEmail === "") {
      setErrorMessage("Please enter an email address.");
      setShowErrorModal(true);
      return;
    }

    try {
      // Create controller and pending invite docs (existing functionality)
      const controllersRef = doc(db, "familyInfo", familyID, "controllers", inviteEmail);
      const pendingInvitesRef = doc(db, "familyInfo", familyID, "pendingControllerInvites", inviteEmail);
      
      // New reference for the invite message
      const inviteMessageRef = doc(
        db,
        "familyInviteMessages",
        inviteEmail,
        "familyIDs",
        familyID
      );

      // Execute all writes
      await Promise.all([
        setDoc(controllersRef, {}),
        setDoc(pendingInvitesRef, {}),
        setDoc(inviteMessageRef, {
          type: "controller",
          timestamp: new Date(),
        }),
      ]);

      setInviteEmail("");
      loadControllers();

      // Fire-and-forget: ask the cloud function to send the invite email.
      // The Firestore invite is the source of truth; the email is a side
      // effect, so we don't fail the UI if this call errors.
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          const response = await fetch(`${API_BASE_URL}/user/sendCoManagerInvite`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ familyID, inviteEmail }),
          });
          if (!response.ok) {
            const body = await response.text();
            console.warn(`sendCoManagerInvite returned ${response.status}: ${body}`);
          }
        }
      } catch (emailError) {
        console.warn("Failed to send co-manager invite email:", emailError);
      }
    } catch (error) {
      console.error("Error inviting controller:", error);
    }
  };

  const giveUpControl = async () => {
    const currentUserEmail = auth.currentUser.email;

    try {
      if (!currentUserAccountUUID) {
        setErrorMessage("Unable to determine your account. Please sign in again.");
        setShowErrorModal(true);
        return;
      }

      // Use accountsInfo with UUID instead of individualInfo with email
      const accountInfoRef = doc(db, "accountsInfo", currentUserAccountUUID);
      const accountDoc = await getDoc(accountInfoRef);

      if (accountDoc.exists()) {
        const data = accountDoc.data();

        // Check if this family is owned by the user by checking the owners subcollection
        // This check comes first as ownership is more fundamental than primary family designation
        const ownerDoc = await getDoc(doc(db, "familyInfo", familyID, "owners", currentUserAccountUUID));
        if (ownerDoc.exists()) {
          setErrorMessage("You cannot give up control of a Hearth you own.");
          setShowErrorModal(true);
          return;
        }

        // Check if this is the primary family
        if (data.primaryFamilyID === familyID) {
          setErrorMessage("You cannot give up control of your primary Hearth.");
          setShowErrorModal(true);
          return;
        }

        // Update the families array in accountsInfo (user is giving up an invited family)
        const updates = {
          families: arrayRemove(familyID),
          numOfFamilies: increment(-1),
          numOfInvitedFamilies: increment(-1)
        };

        const { updateDoc } = await import('firebase/firestore');
        await updateDoc(accountInfoRef, updates);

        // Remove controller access (UUID-based)
        const controllersRef = doc(db, "familyInfo", familyID, "controllers", currentUserAccountUUID);
        const pendingInvitesRef = doc(db, "familyInfo", familyID, "pendingControllerInvites", currentUserEmail);
        const inviteMessageRef = doc(db, "familyInviteMessages", currentUserEmail, "familyIDs", familyID);

        await Promise.all([
          deleteDoc(controllersRef),
          deleteDoc(pendingInvitesRef).catch(() => {}), // Ignore if doesn't exist
          deleteDoc(inviteMessageRef).catch(() => {}) // Ignore if doesn't exist
        ]);

        // Return to main view
        onReturn();
      }
    } catch (error) {
      console.error("Error giving up control:", error);
    }
  };

  return (
    <Card>
      <CardHeader className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-2">
            <Settings className="h-5 w-5 text-slate-900 flex-shrink-0 mt-0.5" />
            <CardTitle className="leading-none">Controllers</CardTitle>
          </div>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            size="sm"
            className="hidden sm:block"
          >
            {isExpanded ? "Hide Details" : "Show Details"}
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardDescription>Manage who can control this device</CardDescription>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            size="sm"
            className="self-start sm:hidden"
          >
            {isExpanded ? "Hide Details" : "Show Details"}
          </Button>
        </div>
      </CardHeader>

      {/* Expandable Content */}
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[1000px]' : 'max-h-0'}`}>
        <CardContent className="space-y-4 p-3">
          {/* Controllers List */}
          {controllers.map((controller) => {
            // Check if this controller is the current user (by UUID or email)
            const isCurrentUser = (controller.isUUIDBased && controller.controllerId === currentUserAccountUUID) ||
                                  controller.email === auth.currentUser?.email;
            // Check if this controller is an owner of this family
            const isOwner = controller.isUUIDBased && ownerUUIDs.includes(controller.controllerId);
            return (
              <div key={controller.controllerId} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-lg gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarFallback className="text-xs">
                      {controller.email.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm sm:text-base truncate">{controller.email}</span>
                      {controller.isPending ? (
                        <Badge variant="outline" className="text-xs text-slate-500 flex-shrink-0">
                          Pending
                        </Badge>
                      ) : isOwner ? (
                        <Badge variant="secondary" className="flex items-center gap-1 bg-slate-100 text-slate-700 text-xs flex-shrink-0">
                          <Crown className="h-3 w-3" />
                          Owner
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex items-center gap-1 text-xs flex-shrink-0">
                          <UserPlus className="h-3 w-3" />
                          Invited
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {isCurrentUser && (
                  <Button
                    onClick={giveUpControl}
                    disabled={isOnlyController}
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    Give Up Control
                  </Button>
                )}
              </div>
            );
          })}

          {/* Invite Section */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="Invite controller by email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
              required
            />
            <Button
              onClick={inviteController}
              className="flex items-center justify-center gap-2 bg-black text-white w-full sm:w-auto"
            >
              <UserPlus className="h-4 w-4" />
              Invite
            </Button>
          </div>
        </CardContent>
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage("");
        }}
      />
    </Card>
  );
}

export default Controllers;
